/**
 * normalizeStatus.js — Frontend
 *
 * Maps any legacy or variant project/task status string into one of the
 * 6 canonical ERP statuses. Mirror of the backend utility — kept in sync.
 *
 * Canonical statuses:
 *   1. Backlog
 *   2. In Progress
 *   3. Under Review
 *   4. Completed
 *   5. On Hold
 *   6. Cancelled
 */

export const CANONICAL_STATUSES = [
    'Backlog',
    'In Progress',
    'Under Review',
    'Completed',
    'On Hold',
    'Cancelled',
    'Incomplete',
];

/**
 * Legacy → canonical alias map (keyed by lowercase for case-insensitive lookup).
 */
const ALIAS_MAP = {
    // Backlog aliases
    planning: 'Backlog',
    'not started': 'Backlog',
    'backlog / upcoming': 'Backlog',
    upcoming: 'Backlog',
    pending: 'Backlog',
    draft: 'Backlog',
    inactive: 'Backlog',
    'not active': 'Backlog',

    // In Progress aliases
    active: 'In Progress',
    'in progress': 'In Progress',
    'in escrow': 'In Progress',

    // Under Review aliases
    'under review': 'Under Review',
    'pending review': 'Under Review',

    // Completed aliases
    completed: 'Completed',
    closed: 'Completed',
    close: 'Completed',
    released: 'Completed',
    archived: 'Completed',

    // On Hold aliases
    'on hold': 'On Hold',
    delayed: 'On Hold',

    // Cancelled aliases
    cancelled: 'Cancelled',
    canceled: 'Cancelled',

    // Incomplete aliases
    incomplete: 'Incomplete',
    'changes requested': 'Incomplete',
};

/**
 * Normalizes a raw status string to one of the 6 canonical ERP statuses.
 *
 * @param {string} rawStatus - The raw status value from API or local state.
 * @param {string} [fallback='Backlog'] - Default when no match is found.
 * @returns {string} One of the 6 canonical status strings.
 */
export function normalizeStatus(rawStatus, fallback = 'Backlog') {
    if (!rawStatus || typeof rawStatus !== 'string') return fallback;

    const trimmed = rawStatus.trim();

    // Already canonical — fast path
    if (CANONICAL_STATUSES.includes(trimmed)) return trimmed;

    // Alias lookup (case-insensitive)
    const mapped = ALIAS_MAP[trimmed.toLowerCase()];
    if (mapped) return mapped;

    return fallback;
}

export default normalizeStatus;
